import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { environment } from '../../../services/global-environment';
import { catchError, finalize, tap } from 'rxjs/operators';
import { Observable, of } from 'rxjs';

interface OrganizationNode {
  id: string;
  name: string;
  type: 'company' | 'department' | 'team' | 'user';
  role?: string;
  children?: OrganizationNode[];
  parentId?: string;
  depart_level?: 'flat' | 'hierarchical'; // 新增 depart_level 屬性
  roles?: Array<{ roleid: string; rolename: string }>; // 新增 roles 屬性
}

// 新增使用者介面定義
interface PermissionUser {
  id: string;
  username: string;
  email: string;
  modules: Array<{ assistantNo: string }>;
  companyid: string;
}

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
  count?: number;
  requestId?: string;
  processingTime?: number;
}

@Component({
  selector: 'app-organization-management',
  templateUrl: './organization-management.component.html',
  styleUrls: ['./organization-management.component.scss'],
  imports: [CommonModule, FormsModule], // 確保有導入 CommonModule 和 FormsModule
  standalone: true
})
export class OrganizationManagementComponent implements OnInit {
  private apiUrl = environment.apiUrl;
  private companyid = environment.companyid;

  @Input() assistantNo = ''; // 預設值

  // 組織數據相關
  organizationData: OrganizationNode | null = null;
  selectedNode: OrganizationNode | null = null;
  editingNode: { name: string; role?: string } = { name: '' };

  // 新增：有權限的使用者列表
  permissionUsers: PermissionUser[] = [];
  isLoadingUsers = false;

  // 可選成員下拉選單（排除已在部門內的）
  get availableUsersForAdd(): PermissionUser[] {

    console.log('可用成員列表:', this.permissionUsers);


    if (!this.parentNode || !this.parentNode.children) return this.permissionUsers;

    console.log("繼續往下");


    const existingUserIds = this.parentNode.children
      .filter(child => child.type === 'user')
      .map(child => child.id);

      console.log("已存在的使用者ID:", existingUserIds);


    return this.permissionUsers.filter(user => !existingUserIds.includes(user.id));
  }

  // 當選擇成員時，同步設置 newNode 的 id 和 name
  onUserSelect(userId: string) {
    const user = this.permissionUsers.find(u => u.id === userId);
    if (user) {
      this.newNode.id = user.id;
      this.newNode.name = user.username;
    } else {
      this.newNode.id = '';
      this.newNode.name = '';
    }
  }

  // 狀態控制
  isLoadingOrg = false;
  isCreatingOrganization = false;
  isAddingNode = false;
  isProcessing = false;
  isEditingDepartmentName = false;
  isEditingUserInfo = false;
  isEditingCompanyName = false;

  // 新增節點相關
  newOrganization = { name: '' };
  newNode: { name: string; id?: string; type: string; role?: string; parentId?: string } = { name: '', type: '' };
  parentNode: OrganizationNode | null = null;
  selectedUserId: string = '';

  // 錯誤處理
  errorMessage = '';

  // 職位選項 //建立預設值
  roleOptions = [
    { value: 'super_admin', label: '超級管理者' },
    { value: 'admin', label: '管理者' },
    { value: 'user', label: '使用者' },
  ];

  // 新增：成功訊息相關屬性
  showSuccessMessage = false;
  successMessage = '';

  // 新增：正在刪除的節點 ID
  deletingNodeId: string | null = null;

  constructor(private http: HttpClient) {}

  ngOnInit() {
    // 同時取得組織資料和有權限的使用者列表
    this.loadInitialData();
  }

  // 新增方法：載入初始資料
  private loadInitialData() {
    // 同時執行兩個 API 呼叫
    this.refreshOrganizationData();
    this.loadPermissionUsers();
  }

  // 新增方法：取得有權限的使用者列表
  private loadPermissionUsers() {
    this.isLoadingUsers = true;
    this.errorMessage = '';

    // 驗證必要參數
    if (!this.companyid || !this.assistantNo) {
      console.warn('缺少必要參數：公司ID或助手編號，跳過載入使用者權限');
      this.isLoadingUsers = false;
      return;
    }

    const url = `${this.apiUrl}/api/users/getuserbypermission`;
    const params = {
      companyid: this.companyid,
      assistantNo: this.assistantNo
    };

    console.log(`載入有權限的使用者列表，URL: ${url}, 參數:`, params);

    this.http.get<ApiResponse<PermissionUser[]>>(url, { params })
      .pipe(
        tap((response) => {
          if (response.success && response.data) {
            this.permissionUsers = response.data;

            console.log('有權限的使用者列表:', this.permissionUsers);

          } else {
            this.permissionUsers = [];
            console.warn('無法取得有權限的使用者列表');
          }
        }),
        catchError((error: HttpErrorResponse) => {
          console.error('載入有權限使用者列表錯誤:', error);
          this.permissionUsers = [];
          // 不設定 errorMessage，避免覆蓋組織資料的錯誤訊息
          return of({ success: false, error: error.message });
        }),
        finalize(() => {
          this.isLoadingUsers = false;
        })
      )
      .subscribe();
  }

  // 新增方法：檢查使用者是否有權限
  hasUserPermission(userId: string): boolean {
    return this.permissionUsers.some(user => user.id === userId);
  }

  // 新增方法：根據 email 尋找使用者
  findUserByEmail(email: string): PermissionUser | undefined {
    return this.permissionUsers.find(user => user.email === email);
  }

  // 新增方法：取得有權限的使用者數量
  getPermissionUserCount(): number {
    return this.permissionUsers.length;
  }

  refreshOrganizationData() {
    this.isLoadingOrg = true;
    this.errorMessage = '';

    // 驗證必要參數
    if (!this.companyid || !this.assistantNo) {
      this.errorMessage = '缺少必要參數：公司ID或助手編號';
      this.isLoadingOrg = false;
      return;
    }

    // 實際 API 呼叫
    const url = `${this.apiUrl}/api/departments/${this.companyid}/${this.assistantNo}`;

    this.http.get<ApiResponse<OrganizationNode>>(url)
      .pipe(
        tap((response) => {

          if (response.success && response.data) {
            this.organizationData = response.data;

            console.log('組織資料載入成功:', this.organizationData);

            // 如果組織資料中有 roles 且長度大於 0，用來更新 roleOptions
            if (this.organizationData.roles && Array.isArray(this.organizationData.roles) && this.organizationData.roles.length > 0) {
              this.roleOptions = this.organizationData.roles.map((role: any) => ({
                value: role.roleid,
                label: role.rolename
              }));
              console.log('職位選項已更新為動態資料:', this.roleOptions);
            }

          } else {
            this.organizationData = null;
          }
        }),
        catchError((error: HttpErrorResponse) => {
          this.errorMessage = error.error?.message || '無法載入組織資料，請稍後再試';
          console.error('載入組織資料錯誤:', error);
          return of({ success: false, error: error.message });
        }),
        finalize(() => {
          this.isLoadingOrg = false;
        })
      )
      .subscribe();
  }

  createNewOrganization() {
    this.isCreatingOrganization = true;
    this.newOrganization = { name: '' };
    this.errorMessage = '';
  }

  cancelCreateOrganization() {
    this.isCreatingOrganization = false;
    this.newOrganization = { name: '' };
  }

  createOrganization() {
    if (!this.newOrganization.name) {
      this.errorMessage = '請輸入組織名稱';
      return;
    }

    this.isProcessing = true;
    this.errorMessage = '';
    const url = `${this.apiUrl}/api/departments/create`;

    // 準備請求資料
    const requestData = {
      companyid: this.companyid,
      assistantNo: this.assistantNo,
      name: this.newOrganization.name
    };

    this.http.post<ApiResponse<OrganizationNode>>(url, requestData)
      .pipe(
        tap((response) => {
          if (response.success && response.data) {
            this.organizationData = response.data;
          }
        }), // 修正這裡的括號閉合
        catchError((error: HttpErrorResponse) => {
          this.errorMessage = error.error?.message || '建立組織失敗，請稍後再試';
          console.error('建立組織錯誤:', error);
          return of({ success: false, error: error.message });
        }),
        finalize(() => {
          this.isProcessing = false;
          if (!this.errorMessage) {
            this.isCreatingOrganization = false;
          }
        })
      )
      .subscribe();
  }

  selectNode(node: OrganizationNode) {
    this.selectedNode = node;
    this.editingNode = { name: node.name, role: node.role };
    this.isAddingNode = false;
    this.resetEditingStates();
    this.errorMessage = '';
  }

  private resetEditingStates() {
    this.isEditingDepartmentName = false;
    this.isEditingUserInfo = false;
    this.isEditingCompanyName = false;
  }

  startEditDepartmentName() {
    this.isEditingDepartmentName = true;
    this.editingNode = { name: this.selectedNode!.name, role: this.selectedNode!.role };
  }

  cancelEditDepartmentName() {
    this.isEditingDepartmentName = false;
    this.editingNode = { name: this.selectedNode!.name, role: this.selectedNode!.role };
  }

  startEditUserInfo() {
    this.isEditingUserInfo = true;
    this.editingNode = { name: this.selectedNode!.name, role: this.selectedNode!.role };
  }

  cancelEditUserInfo() {
    this.isEditingUserInfo = false;
    this.editingNode = { name: this.selectedNode!.name, role: this.selectedNode!.role };
  }

  startEditCompanyName() {
    this.isEditingCompanyName = true;
    this.editingNode = { name: this.selectedNode!.name, role: this.selectedNode!.role };
  }

  cancelEditCompanyName() {
    this.isEditingCompanyName = false;
    this.editingNode = { name: this.selectedNode!.name, role: this.selectedNode!.role };
  }

  // 新增方法：檢查是否可以新增子部門
  canAddSubDepartment(node: OrganizationNode): boolean {
    if (!this.organizationData) return false;

    // 如果是扁平結構 (flat)，只有公司層級可以新增部門
    if (this.organizationData.depart_level === 'flat') {
      return node.type === 'company';
    }

    // 如果是階層結構 (hierarchical) 或未設定，公司和部門都可以新增子部門
    return node.type === 'company' || node.type === 'department';
  }

  prepareAddNode(parentNode: OrganizationNode, event: Event) {
    event.stopPropagation();

    // 檢查是否可以新增子部門
    if (!this.canAddSubDepartment(parentNode)) {
      return;
    }

    this.parentNode = parentNode;
    this.isAddingNode = true;
    this.selectedNode = null;
    this.errorMessage = '';

    if (parentNode.type === 'company') {
      this.newNode = { name: '', type: 'department' };
    } else if (parentNode.type === 'department') {
      this.newNode = { name: '', type: 'department' }; // 部門底下可以新增子部門
    }
  }

  prepareAddUser(parentNode: OrganizationNode, event: Event) {
    event.stopPropagation();
    this.parentNode = parentNode;
    this.isAddingNode = true;
    this.selectedNode = null;
    this.errorMessage = '';
    this.newNode = { name: '', type: 'user', role: '使用者' }; // 預設為使用者
  }

  getNewNodeTypeText(): string {
    switch (this.newNode.type) {
      case 'department': return '部門';
      case 'user': return '成員';
      default: return '節點';
    }
  }

  getParentTypeText(): string {
    if (!this.parentNode) return '';
    switch (this.parentNode.type) {
      case 'company': return '公司';
      case 'department': return '部門';
      default: return '單位';
    }
  }

  getNewNodeDescription(): string {
    if (!this.parentNode) return '';

    if (this.newNode.type === 'department') {
      return `將在「${this.parentNode.name}」底下建立新的部門`;
    } else if (this.newNode.type === 'user') {
      return `將在「${this.parentNode.name}」部門中新增成員`;
    }
    return '';
  }

  cancelAddNode() {
    this.isAddingNode = false;
    this.newNode = { name: '', type: '' };
    this.parentNode = null;
    this.errorMessage = '';
  }

  addNode() {
    if (!this.newNode.name || !this.parentNode) {
      this.errorMessage = '請輸入完整資訊';
      return;
    }

    this.isProcessing = true;
    this.errorMessage = '';
    const url = `${this.apiUrl}/api/departments`;

    // 準備節點資料
    const nodeData: any = {
      name: this.newNode.name,
      type: this.newNode.type,
      children: [] // 新節點應該有空的子節點陣列
    };

    // 如果是用戶類型，添加角色
    if (this.newNode.type === 'user' && this.newNode.role) {
      nodeData.role = this.newNode.role;
      nodeData.id = this.newNode.id; // 要送入使用者ID，這樣才會對的上資料
    }

    // 準備請求資料
    const requestData = {
      companyid: this.companyid,
      assistantNo: this.assistantNo,
      parentId: this.parentNode.id,
      node: nodeData
    };

    this.http.post<ApiResponse<OrganizationNode>>(url, requestData)
      .pipe(
        tap((response) => {
          if (response.success && response.data) {
            this.organizationData = response.data;
          }
        }),
        catchError((error: HttpErrorResponse) => {
          this.errorMessage = error.error?.message || '新增節點失敗，請稍後再試';
          console.error('新增節點錯誤:', error);
          return of({ success: false, error: error.message });
        }),
        finalize(() => {
          this.isProcessing = false;
          if (!this.errorMessage) {
            this.cancelAddNode();
          }
        })
      )
      .subscribe();
  }

  // 修改刪除節點方法，增加成功訊息顯示
  deleteNode(node: OrganizationNode, event: Event) {
    event.stopPropagation();
    this.errorMessage = '';

    // 設定刪除確認訊息，針對不同類型顯示不同提示
    let confirmMessage = '';
    if (node.type === 'user') {
      confirmMessage = `確定要移除「${node.name}」成員嗎？`;
    } else {
      confirmMessage = `確定要刪除「${node.name}」嗎？包含其所有子節點都將被刪除！`;
    }

    if (confirm(confirmMessage)) {
      this.isProcessing = true;
      // 標記正在刪除的節點
      this.deletingNodeId = node.id;

      const url = `${this.apiUrl}/api/departments/${this.companyid}/${this.assistantNo}/${node.id}`;

      this.http.delete<ApiResponse<OrganizationNode>>(url)
        .pipe(
          tap((response) => {
            if (response.success && response.data) {
              // 保存之前的部門引用
              const previousSelectedNodeId = this.selectedNode?.id;

              // 更新整個組織結構
              this.organizationData = response.data;

              // 如果當前選中的節點是被刪除的節點，需要取消選擇
              if (previousSelectedNodeId === node.id) {
                this.selectedNode = null;
              } else if (previousSelectedNodeId && this.organizationData) {
                // 如果之前有選擇節點，並且不是被刪除的節點，嘗試找回並選擇
                const [, updatedNode] = this.findNodeById(this.organizationData, previousSelectedNodeId);
                if (updatedNode) {
                  this.selectedNode = updatedNode;
                  this.editingNode = { name: updatedNode.name, role: updatedNode.role };
                }
              }

              // 顯示成功訊息
              this.showSuccessMessage = true;
              this.successMessage = node.type === 'user'
                ? `成員「${node.name}」已成功移除`
                : `「${node.name}」已成功刪除`;

              // 設定成功訊息的自動關閉
              setTimeout(() => {
                this.showSuccessMessage = false;
              }, 3000);
            }
          }),
          catchError((error: HttpErrorResponse) => {
            this.errorMessage = error.error?.message || '刪除節點失敗，請稍後再試';
            console.error('刪除節點錯誤:', error);
            return of({ success: false, error: error.message });
          }),
          finalize(() => {
            this.isProcessing = false;
            this.deletingNodeId = null; // 清除正在刪除的節點標記
          })
        )
        .subscribe();
    }
  }

  private removeNodeFromTree(nodeToRemove: OrganizationNode) {
    // 保留此方法但不再使用，因為現在使用API完成刪除操作
    // API成功後會返回完整更新的組織架構
    const removeFromChildren = (parent: OrganizationNode) => {
      if (parent.children) {
        parent.children = parent.children.filter(child => {
          if (child.id === nodeToRemove.id) {
            return false;
          }
          removeFromChildren(child);
          return true;
        });
      }
    };

    if (this.organizationData) {
      removeFromChildren(this.organizationData);
    }
  }

  isDirty(): boolean {
    if (!this.selectedNode) return false;
    return this.editingNode.name !== this.selectedNode.name ||
           this.editingNode.role !== this.selectedNode.role;
  }

  updateNode() {
    if (!this.selectedNode || !this.isDirty()) return;

    this.isProcessing = true;
    this.errorMessage = '';

    const url = `${this.apiUrl}/api/departments/${this.companyid}/${this.assistantNo}/${this.selectedNode.id}`;

    // 準備更新資料
    const updates: any = {
      name: this.editingNode.name
    };

    // 只有用戶類型才有角色
    if (this.selectedNode.type === 'user' && this.editingNode.role !== undefined) {
      updates.role = this.editingNode.role;
    }

    // 準備請求資料
    const requestData = {
      updates: updates
    };

    this.http.put<ApiResponse<OrganizationNode>>(url, requestData)
      .pipe(
        tap((response) => {
          if (response.success && response.data) {
            this.organizationData = response.data;
            // 找到更新後的節點並選中它
            const [, updatedNode] = this.findNodeById(this.organizationData!, this.selectedNode!.id);
            if (updatedNode) {
              this.selectedNode = updatedNode;
              this.editingNode = { name: updatedNode.name, role: updatedNode.role };
              this.resetEditingStates(); // 關閉編輯模式
            }
          }
        }),
        catchError((error: HttpErrorResponse) => {
          this.errorMessage = error.error?.message || '更新節點失敗，請稍後再試';
          console.error('更新節點錯誤:', error);
          return of({ success: false, error: error.message });
        }),
        finalize(() => {
          this.isProcessing = false;
        })
      )
      .subscribe();
  }

  // 新增輔助方法，用於尋找節點
  private findNodeById(root: OrganizationNode, id: string): [OrganizationNode | null, OrganizationNode | null] {
    // 如果當前節點匹配，則返回 [null, 當前節點]
    if (root.id === id) {
      return [null, root];
    }

    // 如果有子節點，搜尋子節點
    if (root.children && Array.isArray(root.children)) {
      for (const child of root.children) {
        if (child.id === id) {
          return [root, child]; // 找到了，返回 [父節點, 目標節點]
        }

        // 遞歸搜尋更深層級
        if (child.children && Array.isArray(child.children)) {
          const result = this.findNodeById(child, id);
          if (result[1] !== null) {
            return result; // 找到了，返回結果
          }
        }
      }
    }

    // 未找到
    return [null, null];
  }

  // 新增方法：獲取部門列表（只返回 type 為 'department' 的節點）
  getDepartments(node: OrganizationNode): OrganizationNode[] {
    if (!node.children) return [];
    return node.children.filter(child => child.type === 'department');
  }

  // 新增方法：獲取用戶列表（只返回 type 為 'user' 的節點）
  getUsers(node: OrganizationNode): OrganizationNode[] {
    if (!node.children) return [];
    return node.children.filter(child => child.type === 'user');
  }

  getChildNodes(node: OrganizationNode): OrganizationNode[] {
    return node.children || [];
  }

  editMember(member: OrganizationNode, event: Event) {
    event.stopPropagation();
    this.selectNode(member);
  }

  getDepartmentCount(): number {
    if (!this.organizationData) return 0;
    return this.organizationData.children?.length || 0;
  }

  getTotalMemberCount(): number {
    if (!this.organizationData) return 0;

    const countUsers = (node: OrganizationNode): number => {
      let count = node.type === 'user' ? 1 : 0;
      if (node.children) {
        count += node.children.reduce((sum, child) => sum + countUsers(child), 0);
      }
      return count;
    };

    return countUsers(this.organizationData);
  }
}
